import React, { useState } from "react";
import styled from "styled-components";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import axios from "axios";
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";
import { useEchoMail } from "../../contexts/EchoMailContext";

const Container = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.mainBg};
  color: ${({ theme }) => theme.colors.text};
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.buttonTextOnPrimary};
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function EchoMailPage() {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [newDomain, setNewDomain] = useState("");
  const { domainInfo, reload, getAuthHeaders } = useEchoMail();

  const apiBase = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const authAxios = async () => {
    const headers = await getAuthHeaders();
    return axios.create({ baseURL: apiBase, headers });
  };

  const handleSend = async () => {
    const recipients = to
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (recipients.length === 0 || recipients.length > 20) {
      setMessage("Please provide between 1 and 20 recipient email addresses.");
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const client = await authAxios();
      await client.post("/echomail/send", {
        to: recipients,
        subject,
        htmlContent: content,
      });
      setMessage("Emails sent successfully!");
      setTo("");
      setSubject("");
      setContent("");
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.error || "Failed to send emails");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDomain = async () => {
    if (!newDomain) return;
    setLoading(true);
    setMessage(null);
    try {
      const client = await authAxios();
      await client.post("/echomail/domain", { domain: newDomain });
      setNewDomain("");
      await reload();
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.error || "Failed to start domain auth");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDomain = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const client = await authAxios();
      await client.post("/echomail/domain/verify");
      await reload();
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.error || "Failed to verify domain");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h2>EchoMail – Bulk Email Sender</h2>

      {/* Domain Section */}
      <Field>
        <Label>Your Domain</Label>
        {domainInfo ? (
          <div>
            <p>
              Domain: <strong>{domainInfo.domain}</strong>
            </p>
            <p>
              Status:{" "}
              {domainInfo.verified ? "Verified ✔" : "Pending ↺"}
            </p>
            <h4>DNS Records</h4>
            <ul>
              {domainInfo.dnsRecords?.map((rec) => (
                <li key={rec.host} style={{ marginBottom: "8px" }}>
                  <code>{rec.type}</code> {rec.host} → {rec.data}{" "}
                  <button
                    onClick={() => navigator.clipboard.writeText(`${rec.host}\t${rec.type}\t${rec.data}`)}
                  >
                    Copy
                  </button>
                </li>
              ))}
            </ul>
            {!domainInfo.verified && (
              <Button onClick={handleVerifyDomain} disabled={loading}>
                {loading ? <LoadingSpinner /> : "Verify"}
              </Button>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", gap: "12px" }}>
            <Input
              type="text"
              placeholder="example.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
            />
            <Button onClick={handleCreateDomain} disabled={loading}>
              {loading ? <LoadingSpinner /> : "Authenticate"}
            </Button>
          </div>
        )}
      </Field>

      <hr style={{ margin: "40px 0" }} />

      {/* Send Email Section */}
      <Field>
        <Label>To (max 20 – separate by comma, semicolon, or newline)</Label>
        <TextArea
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="user1@example.com, user2@example.com"
        />
      </Field>

      <Field>
        <Label>Subject</Label>
        <Input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject line"
        />
      </Field>

      <Field>
        <Label>Content</Label>
        <ReactQuill theme="snow" value={content} onChange={setContent} />
      </Field>

      {message && <p>{message}</p>}

      <Button onClick={handleSend} disabled={loading}>
        {loading ? <LoadingSpinner /> : "Send"}
      </Button>
    </Container>
  );
} 